import { getToken } from "@/utils/functions/getToken"
import {
  KeysCountGetAllBeautyOutput,
  KeysCountGetBeautyOutput,
} from "@okeys/shared-utils"
import { useQuery } from "@tanstack/react-query"
import { customAxios } from "../utils/customAxios/customAxios"

/**
 * Hook pour récupérer le nombre de clés de fidélité d'un client dans une boutique
 * @param params - Paramètres contenant clientId et/ou shopId selon le type d'utilisateur
 * @param options - Options pour la query (enabled, refetchInterval, etc.)
 * @returns Les données de fidélité du client, état de chargement et erreurs
 */
export const useKeysCountQuery = (
  params: {
    clientId?: string
  },
  options?: {
    enabled?: boolean
    refetchInterval?: number
    staleTime?: number
  }
) => {
  const { data, isLoading, error, isError, refetch, isFetching } = useQuery({
    queryKey: ["keysCount", params.clientId],
    queryFn: async (): Promise<KeysCountGetBeautyOutput | null> => {
      try {
        if (!params.clientId) {
          return null
        }
        const token = await getToken()

        // Construire les paramètres de query selon les paramètres fournis
        const queryParams = new URLSearchParams()
        queryParams.append("clientId", params.clientId)

        const response = await customAxios.get<KeysCountGetBeautyOutput>(
          `/keys/count?${queryParams.toString()}`,
          {
            headers: {
              Authorization: token || "",
              "Content-Type": "application/json",
            },
          }
        )

        return response.data
      } catch (err) {
        console.error("Error fetching keys count:", err)
        throw err
      }
    },
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes par défaut
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  return {
    keysCount: data,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
  }
}

/**
 * Hook pour récupérer tous les KeysCount d'une boutique
 * @param options - Options pour la query (enabled, refetchInterval, etc.)
 * @returns La liste de tous les KeysCount de la boutique, état de chargement et erreurs
 */
export const useKeysCountGetAllQuery = (options?: {
  enabled?: boolean
  refetchInterval?: number
  staleTime?: number
}) => {
  const { data, isLoading, error, isError, refetch, isFetching } = useQuery({
    queryKey: ["keysCountAll"],
    queryFn: async (): Promise<KeysCountGetAllBeautyOutput | null> => {
      try {
        const token = await getToken()

        const response = await customAxios.get<KeysCountGetAllBeautyOutput>(
          `/keys/count/all`,
          {
            headers: {
              Authorization: token || "",
              "Content-Type": "application/json",
            },
          }
        )

        return response.data
      } catch (err) {
        console.error("Error fetching all keys count:", err)
        throw err
      }
    },
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes par défaut
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  return {
    keysCountData: data,
    keysCountItems: data?.items || [],
    totalCount: data?.count || 0,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
  }
}
